import React, { useEffect, useState } from 'react'
import { Tabs, TabsProps } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { Box, useMediaQuery, useTheme } from '@mui/material'

/**
 * CustomTabs component
 *
 * A wrapper around react-bootstrap Tabs that synchronizes the active tab with URL query parameters.
 * This allows users to share URLs with specific tabs selected and enables browser back/forward navigation.
 *
 * @example
 * ```tsx
 * <CustomTabs
 *   defaultActiveKey="tab1"
 *   queryParamName="activeTab"
 *   validTabs={['tab1', 'tab2', 'tab3']}
 * >
 *   <Tab eventKey="tab1" title="Tab 1">
 *     Content 1
 *   </Tab>
 *   <Tab eventKey="tab2" title="Tab 2">
 *     Content 2
 *   </Tab>
 * </CustomTabs>
 * ```
 */

export interface CustomTabsProps extends TabsProps {
  /**
   * The name of the query parameter to use for storing the active tab in the URL.
   * @default 'activeTab'
   */
  queryParamName?: string

  /**
   * An optional list of valid tab keys. If provided, only these tab keys will be
   * considered valid when reading from the URL. This prevents invalid tabs from being set.
   */
  validTabs?: string[]

  /**
   * Callback function that is called when the active tab changes.
   * Receives the new active tab key as a parameter.
   */
  onTabChange?: (activeKey: string) => void

  /**
   * Optional content to render on the right side of the tab headers row.
   * Typically used for action buttons.
   */
  headerActions?: React.ReactNode
}

const CustomTabs: React.FC<CustomTabsProps> = ({
  queryParamName = 'activeTab',
  validTabs,
  defaultActiveKey,
  onTabChange,
  onSelect,
  activeKey: controlledActiveKey,
  headerActions,
  children,
  ...otherProps
}) => {
  const history = useHistory()
  const [internalActiveKey, setInternalActiveKey] = useState<string>(() => {
    // Initialize from URL query param if available
    const queryParams = new URLSearchParams(history.location.search)
    const tabFromQuery = queryParams.get(queryParamName)

    // Validate against validTabs if provided
    if (tabFromQuery && (!validTabs || validTabs.includes(tabFromQuery))) {
      return tabFromQuery
    }

    // Fall back to defaultActiveKey or first valid tab
    return (defaultActiveKey as string) || (validTabs && validTabs[0]) || ''
  })

  // Use controlled activeKey if provided, otherwise use internal state
  const activeKey =
    controlledActiveKey !== undefined ? controlledActiveKey : internalActiveKey

  // Call onTabChange on mount to sync parent component state with URL
  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search)
    const tabFromQuery = queryParams.get(queryParamName)

    // Determine the active tab
    const initialTab =
      tabFromQuery && (!validTabs || validTabs.includes(tabFromQuery))
        ? tabFromQuery
        : (defaultActiveKey as string) || (validTabs && validTabs[0]) || ''

    // Always call onTabChange on mount to ensure parent state is synced
    if (onTabChange && initialTab) {
      onTabChange(initialTab)
    }
  }, []) // Only run on mount

  // Sync URL when active tab changes
  useEffect(() => {
    if (activeKey) {
      const params = new URLSearchParams(history.location.search)
      const currentTabInUrl = params.get(queryParamName)

      if (currentTabInUrl !== activeKey) {
        params.set(queryParamName, activeKey as string)
        history.replace({
          pathname: history.location.pathname,
          search: params.toString(),
        })
      }
    }
  }, [activeKey, history, queryParamName])

  // Listen to URL changes (e.g., browser back/forward)
  useEffect(() => {
    const unlisten = history.listen(location => {
      const params = new URLSearchParams(location.search)
      const tabFromQuery = params.get(queryParamName)

      // Determine which tab should be active
      let targetTab: string | null = null

      if (tabFromQuery && (!validTabs || validTabs.includes(tabFromQuery))) {
        targetTab = tabFromQuery
      } else if (!tabFromQuery) {
        // If no tab in URL, fall back to default
        targetTab =
          (defaultActiveKey as string) || (validTabs && validTabs[0]) || ''
      }

      if (targetTab) {
        if (controlledActiveKey === undefined) {
          setInternalActiveKey(targetTab)
        }
        if (onTabChange) {
          onTabChange(targetTab)
        }
      }
    })

    return () => unlisten()
  }, [
    history,
    queryParamName,
    validTabs,
    onTabChange,
    controlledActiveKey,
    defaultActiveKey,
  ])

  const handleSelect = (eventKey: string | null) => {
    if (!eventKey) return

    // Validate against validTabs if provided
    if (validTabs && !validTabs.includes(eventKey)) {
      return
    }

    // Update internal state only if not controlled
    if (controlledActiveKey === undefined) {
      setInternalActiveKey(eventKey)
    }

    // Call the custom callback if provided
    if (onTabChange) {
      onTabChange(eventKey)
    }

    // Call the original onSelect if provided
    if (onSelect) {
      onSelect(eventKey, null as any)
    }
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
      }}
    >
      {/* Header actions positioned differently on mobile vs desktop */}
      {headerActions && (
        <Box
          sx={{
            position: isMobile ? 'static' : 'absolute',
            right: 0,
            top: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            zIndex: 10,
            mb: isMobile ? 2 : 0,
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'flex-end' : 'flex-start',
          }}
        >
          {headerActions}
        </Box>
      )}

      {/* Wrapper for scrollable tabs on mobile */}
      <Box
        sx={{
          width: '100%',
          overflowX: isMobile || isTablet ? 'auto' : 'visible',
          overflowY: 'visible',
          // Hide scrollbar but keep functionality
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          // Add padding to prevent cut-off on mobile
          pb: 0.5,
          // Smooth scrolling
          scrollBehavior: 'smooth',
          // Enable momentum scrolling on iOS
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box
          sx={{
            minWidth: isMobile ? 'max-content' : 'auto',
            '& .nav-tabs': {
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              borderBottom: 0,
              gap: { xs: 0, sm: 0.5 },
            },
            '& .nav-tabs > .nav-item': {
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '6px 16px', sm: '4px 15px' },
              marginRight: { xs: '0.5rem', sm: '0.25rem' },
              minWidth: { xs: 'auto', sm: 'auto' },
              // Better touch target size on mobile
              minHeight: { xs: '44px', sm: 'auto' },
              display: 'inline-flex',
              alignItems: 'center',
            },
            '& .nav-tabs > .nav-item:last-child': {
              marginRight: { xs: '1rem', sm: '0.25rem' },
            },
          }}
        >
          <Tabs {...otherProps} activeKey={activeKey} onSelect={handleSelect}>
            {children}
          </Tabs>
        </Box>
      </Box>
    </Box>
  )
}

export default CustomTabs
